import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { isChairmanReadOnly } from '../utils/access';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/rosca';
const MEMBERS_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/members';

const monthNames     = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fullMonthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const PAYMENT_MODES = [
  { value: '', label: '-- Mode of Payment --' },
  { value: 'cash',      label: 'Cash' },
  { value: 'centenary', label: 'Centenary Bank' },
  { value: 'dfcu',      label: 'DFCU Bank' },
];

// ── PDF HELPERS ───────────────────────────────────────────

const addLetterhead = (doc, docTitle, refNumber, entryDate) => {
  doc.setFillColor(44, 62, 80); doc.rect(0, 0, 210, 28, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
  doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
  doc.setFontSize(11); doc.setFont('helvetica', 'normal');
  doc.text(docTitle, 105, 21, { align: 'center' });
  doc.setTextColor(0, 0, 0); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text(`Ref: ${refNumber}`, 15, 36);
  doc.text(`Date: ${entryDate}`, 195, 36, { align: 'right' });
  doc.setDrawColor(44, 62, 80); doc.setLineWidth(0.5); doc.line(15, 39, 195, 39);
  return 45;
};

const addMemberBlock = (doc, y, name, number, phone, email) => {
  doc.setFillColor(240, 240, 240); doc.rect(15, y, 180, 22, 'F');
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
  doc.text('MEMBER DETAILS', 18, y + 6);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  doc.text(`Name: ${name}`, 18, y + 13);
  doc.text(`Member No: ${number}`, 18, y + 19);
  if (phone) doc.text(`Phone: ${phone}`, 110, y + 13);
  if (email) doc.text(`Email: ${email}`, 110, y + 19);
  return y + 28;
};

const addFooter = (doc) => {
  const ph = doc.internal.pageSize.height;
  doc.setFillColor(44, 62, 80); doc.rect(0, ph - 15, 210, 15, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text('Kigezi Association — Official Document — Confidential', 105, ph - 6, { align: 'center' });
};

const generateContributionReceipt = (contrib, cycle) => {
  const doc = new jsPDF();
  const payDate = contrib.payment_date?.slice(0, 10) || new Date().toISOString().split('T')[0];
  const monthLabel = `${monthNames[(contrib.calendar_month || 1) - 1]} ${contrib.calendar_year}`;
  const refNumber = `RCPT-ROSCA-${contrib.member_number}-${contrib.calendar_year}${String(contrib.calendar_month).padStart(2, '0')}`;
  let y = addLetterhead(doc, 'ROSCA CONTRIBUTION RECEIPT', refNumber, payDate);
  y = addMemberBlock(doc, y, contrib.full_name, contrib.member_number, contrib.phone || null, null);
  doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(39, 174, 96);
  doc.text('PAYMENT CONFIRMATION', 105, y + 6, { align: 'center' });
  doc.setTextColor(0, 0, 0); y += 14;
  autoTable(doc, {
    startY: y,
    head: [['Field', 'Details']],
    body: [
      ['ROSCA Cycle', cycle?.cycle_name || '—'],
      ['Contribution Month', monthLabel],
      ['Contribution Month #', String(contrib.contribution_month)],
      ['Amount Due (UGX)', Number(contrib.amount_due).toLocaleString()],
      ['Amount Paid (UGX)', Number(contrib.amount_paid).toLocaleString()],
      ['Outstanding Balance (UGX)', Number(contrib.balance).toLocaleString()],
      ['Payment Status', contrib.status?.toUpperCase()],
      ['Payment Date', payDate],
    ],
    styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }, margin: { left: 15, right: 15 },
  });
  y = doc.lastAutoTable.finalY + 10;
  const isFullyPaid = parseFloat(contrib.balance) === 0;
  doc.setFillColor(isFullyPaid ? 39 : 243, isFullyPaid ? 174 : 156, isFullyPaid ? 96 : 18);
  doc.rect(15, y, 180, 14, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
  doc.text(isFullyPaid ? 'CONTRIBUTION FULLY PAID — THANK YOU' : `PARTIALLY PAID — BALANCE: UGX ${Number(contrib.balance).toLocaleString()}`, 105, y + 9, { align: 'center', maxWidth: 170 });
  doc.setTextColor(0, 0, 0); y += 22;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('This receipt confirms payment received for the ROSCA contribution indicated above. Please retain for your records.', 15, y, { maxWidth: 180 }); y += 18;
  doc.setDrawColor(100, 100, 100); doc.line(15, y, 85, y); doc.line(110, y, 180, y);
  doc.setFontSize(9); doc.text('Received By (Finance Committee)', 15, y + 5); doc.text('Member Acknowledgement', 110, y + 5); doc.text('Date: ___________', 110, y + 11);
  addFooter(doc);
  doc.save(`ROSCA_Receipt_${contrib.member_number}_${contrib.calendar_year}${String(contrib.calendar_month).padStart(2, '0')}.pdf`);
};

// Mirrors backend services/payoutDeductions.js computeDeductionPlan —
// cash from the payout is applied to DEDUCTED lines in list order;
// the uncovered part of a deducted line goes to MIFL (MDN), except the
// MIFL Arrears line itself, whose uncovered part simply stays in MIFL.
const r2 = n => Math.round((Number(n) || 0) * 100) / 100;
const computePayoutPlan = (gross, lines, selectedKeys) => {
  const sel = new Set(selectedKeys || []);
  let cash = r2(gross);
  const plan = (lines || []).map(l => {
    if (!sel.has(l.key)) return { ...l, deducted: false, from_payout: 0, to_mifl: 0, stays_in_mifl: 0 };
    const from_payout = r2(Math.min(cash, l.amount));
    cash = r2(cash - from_payout);
    const unc = r2(l.amount - from_payout);
    return { ...l, deducted: true, from_payout, to_mifl: l.bucket === 'mifl_arrears' ? 0 : unc, stays_in_mifl: l.bucket === 'mifl_arrears' ? unc : 0 };
  });
  const total_deducted = r2(plan.reduce((s, p) => s + p.from_payout, 0));
  return { lines: plan, total_deducted, total_to_mifl: r2(plan.reduce((s, p) => s + p.to_mifl, 0)), net: r2(gross - total_deducted), unmarked_count: plan.filter(p => !p.deducted).length };
};

const generatePayoutReceipt = (payout, cycle, deductions = []) => {
  const doc = new jsPDF();
  const payoutDate = payout.actual_payout_date?.slice(0, 10) || payout.scheduled_date?.slice(0, 10) || new Date().toISOString().split('T')[0];
  const refNumber = `PAYOUT-${cycle?.cycle_name?.replace(/\s+/g, '-') || 'ROSCA'}-SLOT${payout.slot_number}`;
  let y = addLetterhead(doc, 'ROSCA PAYOUT DISBURSEMENT RECEIPT', refNumber, payoutDate);
  y = addMemberBlock(doc, y, payout.primary_member_name, payout.primary_member_number || '—', null, null);
  doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(41, 128, 185);
  doc.text('PAYOUT DISBURSEMENT CONFIRMATION', 105, y + 6, { align: 'center' });
  doc.setTextColor(0, 0, 0); y += 14;
  const tableBody = [
    ['ROSCA Cycle', cycle?.cycle_name || '—'],
    ['Slot Number', `#${payout.slot_number}`],
    ['Scheduled Month', `Month ${payout.scheduled_month}`],
    ['Payout Amount — Primary (UGX)', Number(payout.payout_amount_primary).toLocaleString()],
    ['Disbursement Date', payoutDate],
    ['Payout Status', payout.payout_status?.toUpperCase()],
  ];
  if (payout.secondary_member_name) {
    tableBody.splice(4, 0, ['Co-Beneficiary (Half-Share)', payout.secondary_member_name]);
    tableBody.splice(5, 0, ['Payout Amount — Secondary (UGX)', Number(payout.payout_amount_secondary || 0).toLocaleString()]);
  }
  autoTable(doc, { startY: y, head: [['Field', 'Details']], body: tableBody, styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] }, columnStyles: { 0: { fontStyle: 'bold', cellWidth: 90 } }, margin: { left: 15, right: 15 } });
  y = doc.lastAutoTable.finalY + 8;
  if (deductions.length) {
    const roleName = r => r === 'secondary' ? (payout.secondary_member_name || 'Secondary') : payout.primary_member_name;
    autoTable(doc, {
      startY: y,
      head: [['Member', 'Obligation deducted', 'Due', 'From payout', 'To MIFL']],
      body: deductions.map(d => [roleName(d.beneficiary_role), d.label + (d.pcn_number ? ` → ${d.pcn_number}` : ''), Number(d.amount_due).toLocaleString(), Number(d.amount_deducted).toLocaleString(),
        Number(d.amount_to_mifl) > 0 ? `${Number(d.amount_to_mifl).toLocaleString()} (${d.mdn_number || 'MDN'})` : '—']),
      styles: { fontSize: 8 }, headStyles: { fillColor: [44, 62, 80] }, margin: { left: 15, right: 15 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }
  const grossTotal = Number(payout.payout_amount_primary || 0) + Number(payout.payout_amount_secondary || 0);
  const dedTotal   = Number(payout.deducted_primary || 0) + Number(payout.deducted_secondary || 0);
  const netTotal   = payout.net_payout_primary != null ? Number(payout.net_payout_primary || 0) + Number(payout.net_payout_secondary || 0) : grossTotal;
  if (dedTotal > 0) {
    autoTable(doc, { startY: y, body: [['Gross Payout (UGX)', grossTotal.toLocaleString()], ['Less: Obligations Deducted (UGX)', dedTotal.toLocaleString()], ['Net Disbursed (UGX)', netTotal.toLocaleString()]],
      styles: { fontSize: 10 }, columnStyles: { 0: { fontStyle: 'bold', cellWidth: 90 }, 1: { halign: 'right' } }, margin: { left: 15, right: 15 } });
    y = doc.lastAutoTable.finalY + 8;
  }
  doc.setFillColor(41, 128, 185); doc.rect(15, y, 180, 14, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
  doc.text(`NET PAYOUT DISBURSED — UGX ${netTotal.toLocaleString()}`, 105, y + 9, { align: 'center' });
  doc.setTextColor(0, 0, 0); y += 22;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('This receipt confirms the ROSCA payout disbursement to the member(s) listed above per the cycle schedule.', 15, y, { maxWidth: 180 }); y += 10;
  doc.text(dedTotal > 0 ? 'The member acknowledges receipt of the net payout above, the deductions listed having been applied to their obligations, and confirms no further claim for this slot.' : 'The member acknowledges receipt of the full payout amount and confirms no further claim for this slot.', 15, y, { maxWidth: 180 }); y += 18;
  doc.setDrawColor(100, 100, 100); doc.line(15, y, 85, y); doc.line(110, y, 180, y);
  doc.setFontSize(9); doc.text('Disbursed By (Finance Committee)', 15, y + 5); doc.text('Received By (Member)', 110, y + 5); doc.text('Date: ___________', 110, y + 11);
  addFooter(doc);
  doc.save(`ROSCA_Payout_Slot${payout.slot_number}_${cycle?.cycle_name?.replace(/\s+/g, '_') || 'ROSCA'}.pdf`);
};

// ── COMPONENT ─────────────────────────────────────────────

function Rosca() {
  const [cycles, setCycles]               = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [cycleDetail, setCycleDetail]     = useState(null);
  const [members, setMembers]             = useState([]);
  const [tab, setTab]                     = useState('overview');
  const [contributions, setContributions] = useState([]);
  const [payouts, setPayouts]             = useState([]);
  const [defaulters, setDefaulters]       = useState([]);
  const [swaps, setSwaps]                 = useState([]);
  const [transfers, setTransfers]         = useState([]);
  const [filterMonth, setFilterMonth]     = useState('');
  const [filterYear, setFilterYear]       = useState(new Date().getFullYear());
  const [showNewCycle, setShowNewCycle]   = useState(false);
  const [showRegForm, setShowRegForm]     = useState(false);
  const [editingContrib, setEditingContrib] = useState(null);
  const [editingPayout, setEditingPayout]   = useState(null);
  const [editingReg, setEditingReg]         = useState(null);
  const [editData, setEditData]             = useState({});

  // Contribution pay panel — now includes mode_of_payment and receipt_ref
  const [payingId, setPayingId]   = useState(null);
  const [payData, setPayData]     = useState({ amount_paid: '', payment_date: new Date().toISOString().split('T')[0], mode_of_payment: '', receipt_ref: '' });

  // Disburse modal — replaces window.confirm
  const [disbursingSlot, setDisbursingSlot] = useState(null);
  const [disburseData, setDisburseData]     = useState({ actual_payout_date: new Date().toISOString().split('T')[0], mode_of_payment: '' });
  const [payoutPreview, setPayoutPreview]   = useState(null);   // { beneficiaries: [{ role, full_name, gross, lines }] }
  const [deductKeys, setDeductKeys]         = useState({ primary: [], secondary: [] });
  const [disburseWarning, setDisburseWarning] = useState(false);
  const [disburseBusy, setDisburseBusy]     = useState(false);

  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [customShares, setCustomShares] = useState(false);
  const [lotteryAssignments, setLotteryAssignments] = useState({});
  const [previewStartDate, setPreviewStartDate]     = useState('2026-07-04');
  const [activateForm, setActivateForm] = useState({ start_date: '' });
  const [showActivate, setShowActivate] = useState(false);
  const [slotA, setSlotA]                     = useState(null);
  const [slotB, setSlotB]                     = useState(null);
  const [showSwapConfirm, setShowSwapConfirm] = useState(false);
  const [swapForm, setSwapForm]               = useState({ effective_date: new Date().toISOString().split('T')[0], agreement_ref: '' });
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [transferForm, setTransferForm]         = useState({ transferor_registration_id: '', transferee_id: '', effective_date: new Date().toISOString().split('T')[0], reason: '', agreement_ref: '' });
  const [cycleForm, setCycleForm] = useState({ cycle_name: '', share_price: '', payouts_per_month: 1, due_day: '', registration_deadline: '', ratified_at: '' });
  const [regForm, setRegForm]     = useState({ member_id: '', shares: '1' });
  const readOnly = isChairmanReadOnly();

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchCycles();
    axios.get(MEMBERS_API, { headers }).then(r => setMembers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedCycle) {
      fetchCycleDetail(selectedCycle);
      if (tab === 'contributions') fetchContributions();
      if (tab === 'payouts')       fetchPayouts();
      if (tab === 'lottery')       fetchPayouts();
      if (tab === 'defaulters')    fetchDefaulters();
      if (tab === 'swaps')         { fetchPayouts(); fetchSwaps(); }
      if (tab === 'transfers')     fetchTransfers();
    }
  }, [selectedCycle, tab]);

  const fetchCycles = () => {
    axios.get(`${API}/cycles`, { headers }).then(r => {
      setCycles(r.data);
      if (r.data.length && !selectedCycle) setSelectedCycle(r.data[0].id);
    }).catch(() => {});
  };

  const fetchCycleDetail   = (id) => { axios.get(`${API}/cycles/${id}`, { headers }).then(r => setCycleDetail(r.data)).catch(() => {}); };
  const fetchContributions = () => {
    const params = {};
    if (filterMonth) params.calendar_month = filterMonth;
    if (filterYear)  params.calendar_year  = filterYear;
    axios.get(`${API}/cycles/${selectedCycle}/contributions`, { headers, params }).then(r => setContributions(r.data)).catch(() => {});
  };
  const fetchPayouts    = () => { axios.get(`${API}/cycles/${selectedCycle}/payouts`, { headers }).then(r => setPayouts(r.data)).catch(() => {}); };
  const fetchDefaulters = () => { axios.get(`${API}/cycles/${selectedCycle}/defaulters`, { headers }).then(r => setDefaulters(r.data)).catch(() => {}); };
  const fetchSwaps      = () => { axios.get(`${API}/cycles/${selectedCycle}/swaps`, { headers }).then(r => setSwaps(r.data)).catch(() => {}); };
  const fetchTransfers  = () => { axios.get(`${API}/cycles/${selectedCycle}/transfers`, { headers }).then(r => setTransfers(r.data)).catch(() => {}); };

  useEffect(() => { if (tab === 'contributions' && selectedCycle) fetchContributions(); }, [filterMonth, filterYear]);

  const showMsg = (text, type = 'success') => {
    if (type === 'error') setError(text); else setSuccess(text);
    setTimeout(() => { setError(''); setSuccess(''); }, 5000);
  };

  const createCycle = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/cycles`, cycleForm, { headers });
      showMsg('Cycle created'); setShowNewCycle(false); fetchCycles(); setSelectedCycle(res.data.id);
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const registerMember = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cycles/${selectedCycle}/registrations`, regForm, { headers });
      showMsg('Member registered'); setShowRegForm(false); setRegForm({ member_id: '', shares: '1' }); setCustomShares(false); fetchCycleDetail(selectedCycle);
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const saveEditReg = async (id) => {
    try { await axios.patch(`${API}/registrations/${id}`, editData, { headers }); showMsg('Registration updated'); setEditingReg(null); fetchCycleDetail(selectedCycle); }
    catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const deleteReg = async (id, name) => {
    if (!window.confirm(`Remove registration for ${name}? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/registrations/${id}`, { headers }); showMsg('Registration removed'); fetchCycleDetail(selectedCycle); }
    catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const saveEditContrib = async (id) => {
    try { await axios.patch(`${API}/contributions/${id}`, editData, { headers }); showMsg('Contribution updated'); setEditingContrib(null); fetchContributions(); }
    catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const deletePayment = async (id, name) => {
    if (!window.confirm(`Delete payment for ${name}? This resets the contribution to unpaid.\n\nNOTE: This does NOT reverse the treasury entry — go to Treasury and reverse that separately.`)) return;
    try {
      await axios.delete(`${API}/contributions/${id}/payment`, { headers });
      showMsg('Payment deleted — remember to reverse the treasury entry manually');
      fetchContributions();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const reverseCoverByAssociation = async (id, name) => {
    if (!window.confirm(`Reverse "Cover by Association" for ${name}?\n\nThis deletes the MDN posted to MIFL. Cannot be undone if a PCN has already been allocated against it.`)) return;
    const before = window.prompt(`How much had ${name} already paid on this contribution BEFORE it was covered? (UGX)\n\nEnter 0 if nothing. Leave blank to let the system work it out (only reliable for covers posted after Oct 2026).`, '');
    if (before === null) return;
    const cleaned = String(before).replace(/,/g, '').trim();
    if (cleaned !== '' && (isNaN(Number(cleaned)) || Number(cleaned) < 0)) { showMsg('Enter a valid amount (numbers only)', 'error'); return; }
    try {
      await axios.delete(`${API}/contributions/${id}/cover-by-association`, { headers, params: cleaned === '' ? {} : { paid_before: cleaned } });
      showMsg('Cover-by-association reversed');
      fetchContributions();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const coverByAssociation = async (id, name) => {
    if (!window.confirm(`Cover ${name}'s contribution by association?\n\nThis posts an MDN to MIFL against this member and marks the contribution as paid. The member's financial status will be set to "in arrears" until the MDN is cleared. Post the ROSCA fine separately in the Fines module.`)) return;
    try {
      const res = await axios.post(`${API}/contributions/${id}/cover-by-association`, {}, { headers });
      showMsg(res.data.message || 'Contribution covered by association');
      fetchContributions();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const saveEditPayout = async (id) => {
    try { await axios.patch(`${API}/slots/${id}`, editData, { headers }); showMsg('Payout updated'); setEditingPayout(null); fetchPayouts(); }
    catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  // ── Record ROSCA contribution payment ─────────────────
  const recordPayment = async (contribId) => {
    try {
      await axios.post(`${API}/contributions/${contribId}/pay`, {
        amount_paid:     payData.amount_paid,
        payment_date:    payData.payment_date,
        mode_of_payment: payData.mode_of_payment || undefined,
        receipt_ref:     payData.receipt_ref     || undefined,
      }, { headers });
      showMsg('Payment recorded');
      setPayingId(null);
      setPayData({ amount_paid: '', payment_date: new Date().toISOString().split('T')[0], mode_of_payment: '', receipt_ref: '' });
      fetchContributions();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  // ── Disburse — modal replaces window.confirm ──────────
  const openDisburse = async (slot) => {
    setDisbursingSlot(slot);
    setDisburseData({ actual_payout_date: new Date().toISOString().split('T')[0], mode_of_payment: '' });
    setDeductKeys({ primary: [], secondary: [] });
    setDisburseWarning(false);
    setPayoutPreview(null);
    try {
      const res = await axios.get(`${API}/slots/${slot.id}/payout-preview`, { headers });
      setPayoutPreview(res.data);
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to load obligations', 'error'); setDisbursingSlot(null); }
  };

  const toggleDeduct = (role, key) => {
    setDisburseWarning(false);
    setDeductKeys(prev => {
      const cur = prev[role] || [];
      return { ...prev, [role]: cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key] };
    });
  };

  const disbursePlans = () => (payoutPreview?.beneficiaries || []).map(b => ({ ...b, plan: computePayoutPlan(b.gross, b.lines, deductKeys[b.role] || []) }));

  const submitDisburse = async (force = false) => {
    if (!disbursingSlot || !payoutPreview) return;
    if (!disburseData.mode_of_payment) { showMsg('Select the mode of payment first', 'error'); return; }
    const plans = disbursePlans();
    const needsWarning = plans.some(b => b.plan.net > 0 && b.plan.unmarked_count > 0);
    if (needsWarning && !force) { setDisburseWarning(true); return; }
    setDisburseBusy(true);
    try {
      const res = await axios.post(`${API}/slots/${disbursingSlot.id}/disburse`, { ...disburseData, deductions: deductKeys }, { headers });
      const parts = (res.data.beneficiaries || []).map(b =>
        `${b.member_name}: net UGX ${Number(b.net).toLocaleString()}${b.deducted > 0 ? ` (deducted ${Number(b.deducted).toLocaleString()})` : ''}${b.mdns?.length ? ` — MDN ${b.mdns.join(', ')}` : ''}`);
      showMsg(`Payout disbursed. ${parts.join(' | ')}`);
      setDisbursingSlot(null); setPayoutPreview(null);
      fetchPayouts();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
    finally { setDisburseBusy(false); }
  };

  const closeRegistration = async () => {
    if (!window.confirm('Close registration? This cannot be undone.')) return;
    try {
      const res = await axios.post(`${API}/cycles/${selectedCycle}/close-registration`, {}, { headers });
      showMsg(res.data.message); fetchCycles(); fetchCycleDetail(selectedCycle);
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  // ── Swap ──────────────────────────────────────────────
  const handleSlotSelect = (slot) => {
    if (slot.payout_status === 'disbursed') { showMsg('Cannot swap a disbursed slot', 'error'); return; }
    if (!slotA) { setSlotA(slot); return; }
    if (slotA.id === slot.id) { setSlotA(null); return; }
    if (!slotB) { setSlotB(slot); setShowSwapConfirm(true); return; }
    setSlotB(slot);
  };

  const clearSwapSelection = () => { setSlotA(null); setSlotB(null); setShowSwapConfirm(false); setSwapForm({ effective_date: new Date().toISOString().split('T')[0], agreement_ref: '' }); };

  const submitSwap = async () => {
    if (!slotA || !slotB) return;
    const regA = cycleDetail?.registrations?.find(r => r.id === slotA.primary_registration_id);
    const regB = cycleDetail?.registrations?.find(r => r.id === slotB.primary_registration_id);
    try {
      await axios.post(`${API}/swaps`, { cycle_id: selectedCycle, slot_a_id: slotA.id, slot_b_id: slotB.id, member_a_id: regA?.member_id, member_b_id: regB?.member_id, effective_date: swapForm.effective_date, agreement_ref: swapForm.agreement_ref || null }, { headers });
      showMsg('Swap recorded successfully'); clearSwapSelection(); fetchPayouts(); fetchSwaps();
    } catch (err) { showMsg(err.response?.data?.error || 'Swap failed', 'error'); }
  };

  // ── Transfer ──────────────────────────────────────────
  const getSlotForReg = (regId) => payouts.find(p => p.primary_registration_id === parseInt(regId));

  const submitTransfer = async (e) => {
    e.preventDefault();
    const slot = getSlotForReg(transferForm.transferor_registration_id);
    if (!slot) { showMsg('No payout slot found for this member', 'error'); return; }
    try {
      await axios.post(`${API}/transfers`, { cycle_id: selectedCycle, slot_id: slot.id, transferor_registration_id: parseInt(transferForm.transferor_registration_id), transferee_id: parseInt(transferForm.transferee_id), effective_date: transferForm.effective_date, reason: transferForm.reason || null, agreement_ref: transferForm.agreement_ref || null, transferor_arrears_checked: true, transferee_arrears_checked: true }, { headers });
      showMsg('Transfer recorded successfully'); setShowTransferForm(false); setTransferForm({ transferor_registration_id: '', transferee_id: '', effective_date: new Date().toISOString().split('T')[0], reason: '', agreement_ref: '' }); fetchTransfers(); fetchPayouts(); fetchCycleDetail(selectedCycle);
    } catch (err) { showMsg(err.response?.data?.error || 'Transfer failed', 'error'); }
  };

  // ── Lottery ───────────────────────────────────────────
  const generateLotteryPositions = () => {
    const positions = [];
    const regs = (cycleDetail?.registrations || []).filter(r => r.status === 'active');
    const processedPairIds = new Set();

    regs.forEach(r => {
      const shares    = parseFloat(r.shares);
      const fullSlots = Math.floor(shares);
      const hasFrac   = (shares % 1) === 0.5;
      const isHalfOnly = shares === 0.5;

      // Generate full slots for the whole-number part
      if (!isHalfOnly) {
        for (let i = 1; i <= fullSlots; i++) {
          positions.push({
            key:      `${r.id}_${i}`,
            label:    fullSlots > 1 ? `${r.full_name} (slot ${i}/${fullSlots})` : r.full_name,
            sublabel: `${shares} share(s)`,
            type:     'full',
            primary_registration_id:   r.id,
            secondary_registration_id: null,
          });
        }
      }

      // Generate shared slot for any fractional 0.5 part (covers 0.5, 1.5, 2.5 etc.)
      if ((hasFrac || isHalfOnly) && r.paired_with) {
        const pairKey = [r.id, r.paired_with].sort((a, b) => a - b).join('_');
        if (!processedPairIds.has(pairKey)) {
          processedPairIds.add(pairKey);
          const partner = regs.find(x => x.id === r.paired_with);
          positions.push({
            key:      `pair_${pairKey}`,
            label:    partner ? `${r.full_name} & ${partner.full_name}` : r.full_name,
            sublabel: '½ + ½ shared slot',
            type:     'half_pair',
            primary_registration_id:   r.id,
            secondary_registration_id: r.paired_with,
          });
        }
      }
    });

    return positions;
  };

  const submitLottery = async () => {
    const positions = generateLotteryPositions(); const totalSlots = parseInt(cycleDetail?.cycle?.total_shares || 0);
    const assigned = Object.values(lotteryAssignments).map(v => parseInt(v)).filter(v => !isNaN(v) && v > 0);
    if (assigned.length < positions.length) { showMsg(`Assign slot numbers to all ${positions.length} positions first`, 'error'); return; }
    const uniqueSlots = new Set(assigned);
    if (uniqueSlots.size !== assigned.length) { showMsg('Duplicate slot numbers detected', 'error'); return; }
    const missing = Array.from({ length: totalSlots }, (_, i) => i + 1).filter(n => !uniqueSlots.has(n));
    if (missing.length > 0) { showMsg(`Missing slot numbers: ${missing.join(', ')}`, 'error'); return; }
    const slots = positions.map(p => ({ slot_number: parseInt(lotteryAssignments[p.key]), slot_type: p.type, primary_registration_id: p.primary_registration_id, secondary_registration_id: p.secondary_registration_id || null }));
    try {
      const res = await axios.post(`${API}/cycles/${selectedCycle}/slots`, { slots }, { headers });
      showMsg(`Lottery recorded — ${res.data.slots?.length || slots.length} slots assigned`);
      setLotteryAssignments({}); fetchCycles(); fetchCycleDetail(selectedCycle); fetchPayouts();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to save lottery', 'error'); }
  };

  const activateCycle = async (e) => {
    e.preventDefault();
    if (!activateForm.start_date) { showMsg('Start date is required', 'error'); return; }
    try {
      const res = await axios.post(`${API}/cycles/${selectedCycle}/activate`, activateForm, { headers });
      showMsg(`Cycle activated — ${res.data.contribution_records_created} contribution records created`);
      setShowActivate(false); fetchCycles(); fetchCycleDetail(selectedCycle);
    } catch (err) { showMsg(err.response?.data?.error || 'Activation failed', 'error'); }
  };

  // ── Calendar month from scheduled month number ────────
  const getCalendarMonth = (scheduledMonth) => {
    const startStr = cycle?.start_date || previewStartDate;
    if (!startStr) return `Month ${scheduledMonth}`;
    const start = new Date(startStr);
    const d     = new Date(start.getFullYear(), start.getMonth() + (scheduledMonth - 1), 1);
    return `${fullMonthNames[d.getMonth()]} ${d.getFullYear()}`;
  };

  // ── Lottery Draw PDF ──────────────────────────────────
  const generateLotteryPDF = () => {
    const doc      = new jsPDF();
    const today    = new Date().toISOString().split('T')[0];
    const cycleName = cycle?.cycle_name || '2026-2027';

    // Letterhead
    doc.setFillColor(44, 62, 80); doc.rect(0, 0, 210, 28, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
    doc.setFontSize(11); doc.setFont('helvetica', 'normal');
    doc.text(`ROSCA LOTTERY DRAW — ${cycleName}`, 105, 21, { align: 'center' });
    doc.setTextColor(0, 0, 0); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    doc.text(`Date: ${today}`, 195, 36, { align: 'right' });
    doc.setDrawColor(44, 62, 80); doc.setLineWidth(0.5); doc.line(15, 39, 195, 39);

    // Cycle summary block
    let y = 46;
    doc.setFillColor(240, 240, 240); doc.rect(15, y, 180, 22, 'F');
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
    doc.text('CYCLE DETAILS', 18, y + 6);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
    doc.text(`Share Price: UGX ${Number(cycle?.share_price || 0).toLocaleString()}`, 18, y + 13);
    doc.text(`Monthly Pool: UGX ${Number(cycle?.total_monthly_pool || 0).toLocaleString()}`, 18, y + 19);
    doc.text(`Total Shares: ${cycle?.total_shares || 26}`, 110, y + 13);
    doc.text(`Duration: ${cycle?.cycle_duration_months || 13} months`, 110, y + 19);
    y += 28;

    // Payout schedule table
    const sorted  = [...payouts].sort((a, b) => a.slot_number - b.slot_number);
    const tableBody = sorted.map(p => [
      `#${p.slot_number}`,
      p.slot_type === 'half_pair'
        ? `${p.primary_member_name}\n+ ${p.secondary_member_name || ''}`
        : p.primary_member_name,
      p.slot_type === 'half_pair' ? 'SHARED' : 'FULL',
      getCalendarMonth(p.scheduled_month),
      `UGX ${Number(p.payout_amount_primary).toLocaleString()}`,
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Slot', 'Member(s)', 'Type', 'Payout Month', 'Payout Amount (UGX)']],
      body: tableBody,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [142, 68, 173], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { cellWidth: 14, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 70 },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 38 },
        4: { cellWidth: 38, halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: 15, right: 15 },
    });

    y = doc.lastAutoTable.finalY + 12;

    // Summary totals
    const totalPayout = sorted.reduce((s, p) => s + parseFloat(p.payout_amount_primary || 0) + parseFloat(p.payout_amount_secondary || 0), 0);
    doc.setFillColor(44, 62, 80); doc.rect(15, y, 180, 12, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    doc.text(`Total Payouts: ${sorted.length} slots  |  Total Pool: UGX ${Number(totalPayout).toLocaleString()}`, 105, y + 8, { align: 'center' });
    doc.setTextColor(0, 0, 0); y += 20;

    // Attestation
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text('This lottery draw was conducted transparently before all members of Kigezi Association.', 15, y, { maxWidth: 180 });
    y += 8;
    doc.text('The payout order above is final and binding for the duration of the cycle.', 15, y, { maxWidth: 180 });
    y += 18;
    doc.setDrawColor(100, 100, 100);
    doc.line(15, y, 90, y); doc.line(115, y, 195, y);
    doc.setFontSize(9);
    doc.text('Chairperson', 15, y + 5);
    doc.text('Date: ___________', 15, y + 11);
    doc.text('Secretary General', 115, y + 5);
    doc.text('Date: ___________', 115, y + 11);

    // Footer
    const ph = doc.internal.pageSize.height;
    doc.setFillColor(44, 62, 80); doc.rect(0, ph - 15, 210, 15, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text('Kigezi Association — Official Document — Confidential', 105, ph - 6, { align: 'center' });

    doc.save(`ROSCA_LotteryDraw_${cycleName.replace(/\s+/g, '_')}.pdf`);
  };

  const cycle = cycleDetail?.cycle;

  const statusBadge = (s) => {
    const map = { active: '#27ae60', registration: '#2980b9', lottery_pending: '#f39c12', lottery_done: '#8e44ad', completed: '#95a5a6', paid: '#27ae60', partial: '#f39c12', unpaid: '#e74c3c', disbursed: '#27ae60', pending: '#3498db', blocked: '#e74c3c', postponed: '#95a5a6', withdrawn: '#95a5a6' };
    return <span style={{ background: map[s] || '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>{s?.replace(/_/g, ' ').toUpperCase()}</span>;
  };

  const fineTag = (c) => {
    if (!c.fine_id) return null;
    const isT2 = c.fine_tier2_start && new Date() >= new Date(c.fine_tier2_start);
    return <span style={{ background: isT2 ? '#c0392b' : '#e67e22', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', marginLeft: '6px' }}>{isT2 ? 'T2 Fine' : 'T1 Fine'} UGX {Number(c.fine_balance).toLocaleString()}</span>;
  };

  // "Cover by Association" only makes sense for a contribution whose due
  // date has already passed — a future month hasn't failed yet, so there's
  // nothing to cover. This compares the contribution's calendar month/year
  // against today's calendar month/year (not the cycle due_day, since we
  // only have month granularity here).
  const isPastDue = (c) => {
    const today = new Date();
    const dueYear = parseInt(c.calendar_year);
    const dueMonth = parseInt(c.calendar_month);
    if (dueYear < today.getFullYear()) return true;
    if (dueYear === today.getFullYear() && dueMonth < (today.getMonth() + 1)) return true;
    if (dueYear === today.getFullYear() && dueMonth === (today.getMonth() + 1)) return true; // due this month = coverable once unpaid
    return false;
  };

  const inp       = { padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' };
  const inlineInp = { padding: '4px 6px', border: '1px solid #f39c12', borderRadius: '4px', fontSize: '12px', background: '#fffde7', width: '100%' };
  const btn       = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '7px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' });
  const smBtn     = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' });

  const activeRegistrations    = (cycleDetail?.registrations || []).filter(r => r.status === 'active');
  const selectedTransferorReg  = activeRegistrations.find(r => r.id === parseInt(transferForm.transferor_registration_id));
  const selectedTransferorSlot = selectedTransferorReg ? getSlotForReg(selectedTransferorReg.id) : null;

  return (
    <div style={{ padding: '20px' }}>

      {/* ── DISBURSE MODAL (with obligation deductions) ── */}
      {disbursingSlot && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', width: '100%', maxWidth: '680px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 6px', color: '#1a5276' }}>Disburse Payout — Slot #{disbursingSlot.slot_number} (Month {disbursingSlot.scheduled_month})</h3>
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '14px' }}>Click <b>Deduct</b> on each obligation to net it off the payout. A deducted line the payout can't fully cover is transferred to MIFL as an MDN.</div>

            {!payoutPreview && <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>Loading obligations…</div>}

            {payoutPreview && disbursePlans().map(b => (
              <div key={b.role} style={{ border: '1px solid #d6eaf8', borderRadius: '8px', marginBottom: '14px', overflow: 'hidden' }}>
                <div style={{ background: '#eaf4fb', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span><strong>{b.full_name}</strong> <span style={{ color: '#7f8c8d' }}>({b.member_number}){b.role === 'secondary' ? ' — co-beneficiary' : ''}</span></span>
                  <span>Gross: <strong>UGX {Number(b.gross).toLocaleString()}</strong></span>
                </div>
                {b.plan.lines.length === 0 && <div style={{ padding: '10px 14px', fontSize: '12px', color: '#27ae60' }}>No obligations due — full payout.</div>}
                {b.plan.lines.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead><tr style={{ background: '#f8f9f9', color: '#7f8c8d' }}>
                      <th style={{ textAlign: 'left', padding: '6px 10px' }}>Obligation (due)</th>
                      <th style={{ textAlign: 'right', padding: '6px 10px' }}>Due</th>
                      <th style={{ textAlign: 'right', padding: '6px 10px' }}>From payout</th>
                      <th style={{ textAlign: 'right', padding: '6px 10px' }}>→ MIFL</th>
                      <th style={{ padding: '6px 10px' }}></th>
                    </tr></thead>
                    <tbody>
                      {b.plan.lines.map(l => (
                        <tr key={l.key} style={{ borderTop: '1px solid #f0f0f0', background: l.deducted ? '#eafaf1' : 'white' }}>
                          <td style={{ padding: '6px 10px' }}>{l.label}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>{Number(l.amount).toLocaleString()}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', color: '#1e8449' }}>{l.deducted ? Number(l.from_payout).toLocaleString() : '—'}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', color: '#c0392b' }}>{l.deducted && l.to_mifl > 0 ? `${Number(l.to_mifl).toLocaleString()} MDN` : (l.deducted && l.stays_in_mifl > 0 ? `${Number(l.stays_in_mifl).toLocaleString()} stays` : '—')}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                            {!readOnly && <button onClick={() => toggleDeduct(b.role, l.key)} style={smBtn(l.deducted ? '#95a5a6' : '#2980b9')}>{l.deducted ? 'Undo' : 'Deduct'}</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div style={{ background: '#fdfefe', borderTop: '1px solid #d6eaf8', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', fontSize: '13px' }}>
                  <span>Deducted: <strong>UGX {Number(b.plan.total_deducted).toLocaleString()}</strong></span>
                  {b.plan.total_to_mifl > 0 && <span style={{ color: '#c0392b' }}>To MIFL (MDN): <strong>UGX {Number(b.plan.total_to_mifl).toLocaleString()}</strong></span>}
                  <span>Net to disburse: <strong style={{ color: '#1e8449', fontSize: '15px' }}>UGX {Number(b.plan.net).toLocaleString()}</strong></span>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '4px' }}>Mode of Payment (account to debit) *</label>
                <select style={{ ...inp, width: '100%' }} value={disburseData.mode_of_payment} onChange={e => setDisburseData({ ...disburseData, mode_of_payment: e.target.value })}>
                  {PAYMENT_MODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: '160px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '4px' }}>Actual Payout Date</label>
                <input type="date" style={{ ...inp, width: '100%' }} value={disburseData.actual_payout_date} onChange={e => setDisburseData({ ...disburseData, actual_payout_date: e.target.value })} />
              </div>
            </div>

            {disburseWarning && (
              <div style={{ background: '#fef5e7', border: '1px solid #f5b041', borderRadius: '6px', padding: '12px 14px', marginBottom: '14px', fontSize: '13px' }}>
                <strong style={{ color: '#b9770e' }}>⚠ Unmarked obligations while payout balance is still available</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                  {disbursePlans().filter(b => b.plan.net > 0 && b.plan.unmarked_count > 0).map(b => (
                    <li key={b.role}>{b.full_name}: {b.plan.lines.filter(l => !l.deducted).map(l => `${l.label} (UGX ${Number(l.amount).toLocaleString()})`).join('; ')} — net still UGX {Number(b.plan.net).toLocaleString()}</li>
                  ))}
                </ul>
                <div style={{ marginTop: '6px', color: '#7f8c8d' }}>These will remain outstanding. Go back and deduct them, or disburse anyway.</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setDisbursingSlot(null); setPayoutPreview(null); }} style={btn('#95a5a6')}>Cancel</button>
              {!readOnly && payoutPreview && !disburseWarning && <button disabled={disburseBusy} onClick={() => submitDisburse(false)} style={btn('#27ae60')}>✓ Confirm Disbursement</button>}
              {!readOnly && payoutPreview && disburseWarning && <>
                <button onClick={() => setDisburseWarning(false)} style={btn('#2980b9')}>← Go Back</button>
                <button disabled={disburseBusy} onClick={() => submitDisburse(true)} style={btn('#e67e22')}>Disburse Anyway</button>
              </>}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, color: '#2c3e50' }}>ROSCA Management</h2>
        {!readOnly && <button onClick={() => setShowNewCycle(!showNewCycle)} style={btn('#2c3e50')}>{showNewCycle ? 'Cancel' : '+ New Cycle'}</button>}
      </div>

      {error   && <div style={{ background: '#e74c3c', color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '12px' }}>{error} <span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>X</span></div>}
      {success && <div style={{ background: '#2ecc71', color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '12px' }}>{success} <span onClick={() => setSuccess('')} style={{ cursor: 'pointer', float: 'right' }}>X</span></div>}

      {/* New Cycle Form */}
      {!readOnly && showNewCycle && (
        <form onSubmit={createCycle} style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <h3 style={{ width: '100%', margin: '0 0 8px', color: '#2c3e50' }}>New ROSCA Cycle</h3>
          {[['Cycle Name', 'cycle_name', 'text'], ['Share Price (UGX)', 'share_price', 'number'], ['Payouts/Month', 'payouts_per_month', 'number'], ['Due Day', 'due_day', 'number']].map(([label, key, type]) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '160px' }}>
              <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>{label}</label>
              <input type={type} style={inp} value={cycleForm[key]} onChange={e => setCycleForm({ ...cycleForm, [key]: e.target.value })} required />
            </div>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '160px' }}>
            <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>Registration Deadline</label>
            <input type="date" style={inp} value={cycleForm.registration_deadline} onChange={e => setCycleForm({ ...cycleForm, registration_deadline: e.target.value })} required />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
            <button type="submit" style={btn('#27ae60')}>Create Cycle</button>
            <button type="button" onClick={() => setShowNewCycle(false)} style={btn('#95a5a6')}>Cancel</button>
          </div>
        </form>
      )}

      {/* Cycle Selector */}
      {cycles.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {cycles.map(c => (
            <button key={c.id} onClick={() => setSelectedCycle(c.id)} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: selectedCycle === c.id ? '#2c3e50' : '#ecf0f1', color: selectedCycle === c.id ? 'white' : '#2c3e50', fontWeight: 'bold', fontSize: '13px' }}>
              {c.cycle_name} {statusBadge(c.status)}
            </button>
          ))}
        </div>
      )}

      {/* Cycle KPIs */}
      {cycle && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {[
            { label: 'Share Price', value: `UGX ${Number(cycle.share_price).toLocaleString()}`, color: '#2980b9' },
            { label: 'Monthly Pool', value: `UGX ${Number(cycle.total_monthly_pool || 0).toLocaleString()}`, color: '#27ae60' },
            { label: 'Total Shares', value: cycle.total_shares || '—', color: '#8e44ad' },
            { label: 'Duration', value: cycle.cycle_duration_months ? `${cycle.cycle_duration_months} months` : '—', color: '#e67e22' },
            { label: 'Members', value: cycleDetail?.summary?.total_members || 0, color: '#2c3e50' },
          ].map((k, i) => (
            <div key={i} style={{ background: k.color, color: 'white', padding: '12px 16px', borderRadius: '8px', minWidth: '130px' }}>
              <div style={{ fontSize: '10px', opacity: 0.85 }}>{k.label}</div>
              <div style={{ fontSize: '18px', fontWeight: '700' }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      {selectedCycle && (
        <div style={{ display: 'flex', gap: '0', marginBottom: '16px', borderBottom: '2px solid #ecf0f1', flexWrap: 'wrap' }}>
          {(() => {
            const cycleStatus = cycle?.status || cycles.find(c => c.id === selectedCycle)?.status || '';
            const showLottery = cycleStatus === 'lottery_pending' || cycleStatus === 'lottery_done';
            return [
              ['overview', 'Overview'], ['registrations', 'Registrations'],
              ...(showLottery ? [['lottery', '🎲 Lottery']] : []),
              ['contributions', 'Contributions'], ['payouts', 'Payouts'],
              ['swaps', 'Swaps'], ['transfers', 'Transfers'], ['defaulters', 'Defaulters']
            ].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)} style={{ padding: '10px 16px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', background: tab === key ? (key === 'lottery' ? '#8e44ad' : '#2c3e50') : 'transparent', color: tab === key ? 'white' : (key === 'lottery' ? '#8e44ad' : '#7f8c8d'), borderRadius: '4px 4px 0 0' }}>{label}</button>
            ));
          })()}
        </div>
      )}

      {/* OVERVIEW TAB */}
      {tab === 'overview' && cycle && (
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '20px', minWidth: '300px' }}>
            <h3 style={{ margin: '0 0 12px', color: '#2c3e50' }}>Cycle Details</h3>
            {[['Name', cycle.cycle_name], ['Status', cycle.status?.replace(/_/g, ' ')], ['Share Price', `UGX ${Number(cycle.share_price).toLocaleString()}`], ['Due Day', `Day ${cycle.due_day} of month`], ['Reg Deadline', cycle.registration_deadline?.slice(0, 10)], ['Start Date', cycle.start_date?.slice(0, 10) || '—'], ['End Date', cycle.end_date?.slice(0, 10) || '—']].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f9f9f9', fontSize: '13px' }}>
                <span style={{ color: '#666' }}>{label}</span><span style={{ fontWeight: '600' }}>{value}</span>
              </div>
            ))}
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {(() => { const cs = cycle?.status || cycles.find(c=>c.id===selectedCycle)?.status||''; return (<>
                {!readOnly && cs === 'registration' && <button onClick={closeRegistration} style={btn('#e74c3c')}>Close Registration</button>}
                {cs === 'lottery_pending' && <button onClick={() => setTab('lottery')} style={btn('#8e44ad')}>🎲 Run Lottery</button>}
                {!readOnly && cs === 'lottery_done' && <button onClick={() => setShowActivate(!showActivate)} style={btn('#27ae60')}>{showActivate ? 'Cancel' : '▶ Activate Cycle'}</button>}
              </>); })()}
            </div>
            {!readOnly && showActivate && (cycle?.status === 'lottery_done' || cycles.find(c=>c.id===selectedCycle)?.status === 'lottery_done') && (
              <form onSubmit={activateCycle} style={{ marginTop: '16px', background: '#eafaf1', border: '1px solid #a9dfbf', borderRadius: '8px', padding: '16px' }}>
                <div style={{ fontWeight: '700', color: '#1e8449', marginBottom: '12px', fontSize: '14px' }}>Activate Cycle</div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Cycle Start Date *</label>
                    <input type='date' style={inp} value={activateForm.start_date} onChange={e => setActivateForm({ start_date: e.target.value })} required />
                  </div>
                  <button type='submit' style={btn('#27ae60')}>Confirm Activation</button>
                </div>
              </form>
            )}
          </div>
          <div style={{ background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '20px', minWidth: '280px', flex: 1 }}>
            <h3 style={{ margin: '0 0 12px', color: '#2c3e50' }}>Registration Summary</h3>
            {cycleDetail?.summary && (<>
              {[['Active Members', cycleDetail.summary.total_members, '#27ae60'], ['Opted Out', cycleDetail.summary.opted_out, '#e74c3c'], ['Total Shares', cycleDetail.summary.total_shares, '#2c3e50']].map(([label, value, color]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f9f9f9', fontSize: '13px' }}>
                  <span style={{ color: '#666' }}>{label}</span><span style={{ fontWeight: '600', color }}>{value}</span>
                </div>
              ))}
              {cycleDetail.unpaired_half_shares > 0 && <div style={{ background: '#ffeaa7', padding: '8px 12px', borderRadius: '6px', marginTop: '12px', fontSize: '12px', color: '#e17055' }}>{cycleDetail.unpaired_half_shares} unpaired half-share member(s)</div>}
            </>)}
          </div>
        </div>
      )}

      {/* REGISTRATIONS TAB */}
      {tab === 'registrations' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '13px', color: '#666' }}>{cycleDetail?.registrations?.length || 0} registrations</span>
            {!readOnly && cycle?.status === 'registration' && <button onClick={() => setShowRegForm(!showRegForm)} style={btn('#27ae60')}>{showRegForm ? 'Cancel' : '+ Register Member'}</button>}
          </div>
          {!readOnly && showRegForm && (
            <form onSubmit={registerMember} style={{ background: '#f0faf4', border: '1px solid #a9dfbf', padding: '16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '200px' }}>
                <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>Member *</label>
                <select style={inp} value={regForm.member_id} onChange={e => setRegForm({ ...regForm, member_id: e.target.value })} required>
                  <option value=''>-- Select --</option>
                  {members.filter(m => { const reg = (cycleDetail?.registrations || []).find(r => r.member_id === m.id); return !reg || reg.status === 'opted_out'; }).map(m => <option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>Shares {customShares && <span style={{ color: '#e67e22' }}>(custom)</span>}</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {!customShares ? (
                    <select style={inp} value={regForm.shares} onChange={e => setRegForm({ ...regForm, shares: e.target.value })}>
                      {['0', '0.5', '1', '2', '3'].map(s => <option key={s} value={s}>{s} {s === '0' ? '(Opted Out)' : ''}</option>)}
                    </select>
                  ) : (
                    <input type='number' step='0.5' min='0' max='10' style={{ ...inp, width: '100px' }} value={regForm.shares} onChange={e => setRegForm({ ...regForm, shares: e.target.value })} placeholder='e.g. 2.5' />
                  )}
                  <button type="button" onClick={() => { setCustomShares(!customShares); setRegForm({ ...regForm, shares: '1' }); }} style={{ padding: '8px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', background: customShares ? '#27ae60' : '#95a5a6', color: 'white' }}>{customShares ? '← Presets' : '✎ Custom'}</button>
                </div>
              </div>
              {cycle?.share_price && <div style={{ padding: '8px 12px', background: '#d5f5e3', borderRadius: '6px', fontSize: '13px', color: '#1e8449' }}>Monthly: <strong>UGX {(parseFloat(regForm.shares || 0) * parseFloat(cycle.share_price)).toLocaleString()}</strong></div>}
              <button type="submit" style={btn('#27ae60')}>Register</button>
            </form>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ background: '#2c3e50', color: 'white' }}>
                <th style={th}>Member</th><th style={th}>Shares</th><th style={th}>Monthly</th><th style={th}>Status</th><th style={th}>Agreement</th><th style={th}>Paired With</th><th style={th}>Actions</th>
              </tr></thead>
              <tbody>
                {(cycleDetail?.registrations || []).length === 0 && <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: '#95a5a6' }}>No registrations yet</td></tr>}
                {(cycleDetail?.registrations || []).map((r, i) => (
                  editingReg === r.id ? (
                    <tr key={r.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                      <td style={td}><strong>{r.full_name}</strong><br /><span style={{ color: '#95a5a6', fontSize: '11px' }}>{r.member_number}</span></td>
                      <td style={td}><input type='number' step='0.5' min='0' max='10' value={editData.shares} onChange={e => setEditData({ ...editData, shares: e.target.value })} style={{ ...inlineInp, width: '80px' }} /></td>
                      <td style={td}>UGX {Math.round(parseFloat(editData.shares || 0) * parseFloat(cycle?.share_price || 0)).toLocaleString()}</td>
                      <td style={td}><select value={editData.status} onChange={e => setEditData({ ...editData, status: e.target.value })} style={inlineInp}>{['active','opted_out','withdrawn'].map(s => <option key={s} value={s}>{s}</option>)}</select></td>
                      <td style={td}><select value={editData.agreement_signed} onChange={e => setEditData({ ...editData, agreement_signed: e.target.value === 'true' })} style={inlineInp}><option value="false">No</option><option value="true">Yes</option></select></td>
                      <td style={td}>{r.paired_member_name || '—'}</td>
                      <td style={td}>{!readOnly && <button onClick={() => saveEditReg(r.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>}<button onClick={() => setEditingReg(null)} style={smBtn('#95a5a6')}>Cancel</button></td>
                    </tr>
                  ) : (
                    <tr key={r.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={td}><strong>{r.full_name}</strong><br /><span style={{ color: '#95a5a6', fontSize: '11px' }}>{r.member_number}</span></td>
                      <td style={td}>{parseFloat(r.shares)}</td>
                      <td style={td}>UGX {Number(r.monthly_contribution).toLocaleString()}</td>
                      <td style={td}>{statusBadge(r.status)}</td>
                      <td style={td}>{r.agreement_signed ? 'Signed' : 'Pending'}</td>
                      <td style={td}>{r.paired_member_name || '—'}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {!readOnly && <button onClick={() => { setEditingReg(r.id); setEditData({ shares: String(parseFloat(r.shares)), status: r.status, agreement_signed: r.agreement_signed }); }} style={smBtn('#e67e22')}>Edit</button>}
                          {!readOnly && cycle?.status === 'registration' && <button onClick={() => deleteReg(r.id, r.full_name)} style={smBtn('#e74c3c')}>Remove</button>}
                        </div>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CONTRIBUTIONS TAB */}
      {tab === 'contributions' && (
        <div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-end', background: '#f9f9f9', padding: '12px', borderRadius: '8px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Month</label>
              <select style={inp} value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
                <option value=''>All Months</option>
                {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Year</label>
              <input type="number" style={{ ...inp, width: '90px' }} value={filterYear} onChange={e => setFilterYear(e.target.value)} />
            </div>
            <button onClick={fetchContributions} style={btn('#2c3e50')}>Filter</button>
            <button onClick={() => { setFilterMonth(''); setFilterYear(new Date().getFullYear()); setTimeout(fetchContributions, 100); }} style={btn('#95a5a6')}>Clear</button>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
            {[{ label: 'Paid', count: contributions.filter(c => c.status === 'paid').length, color: '#27ae60' }, { label: 'Partial', count: contributions.filter(c => c.status === 'partial').length, color: '#f39c12' }, { label: 'Unpaid', count: contributions.filter(c => c.status === 'unpaid').length, color: '#e74c3c' }].map((s, i) => (
              <div key={i} style={{ background: s.color, color: 'white', padding: '8px 14px', borderRadius: '6px', fontSize: '13px' }}>{s.label}: <strong>{s.count}</strong></div>
            ))}
            <div style={{ background: '#ecf0f1', padding: '8px 14px', borderRadius: '6px', fontSize: '13px', color: '#2c3e50' }}>
              Total Paid: <strong>UGX {contributions.reduce((s, c) => s + parseFloat(c.amount_paid), 0).toLocaleString()}</strong>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#2c3e50', color: 'white' }}>
                  <th style={th}>Member</th><th style={th}>Month</th><th style={th}>Due</th><th style={th}>Paid</th><th style={th}>Balance</th><th style={th}>Status</th><th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contributions.length === 0 && <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: '#95a5a6' }}>No contributions for this period</td></tr>}
                {contributions.map((c, i) => {

                  // EDIT ROW
                  if (editingContrib === c.id) return (
                    <tr key={c.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                      <td style={td}><strong>{c.full_name}</strong></td>
                      <td style={td}>{monthNames[(c.calendar_month || 1) - 1]} {c.calendar_year}</td>
                      <td style={td}><input type="number" value={editData.amount_due} onChange={e => setEditData({ ...editData, amount_due: e.target.value })} style={inlineInp} /></td>
                      <td style={td}><input type="number" value={editData.amount_paid} onChange={e => setEditData({ ...editData, amount_paid: e.target.value })} style={inlineInp} /></td>
                      <td style={td}>{Math.max(0, parseFloat(editData.amount_due || 0) - parseFloat(editData.amount_paid || 0)).toLocaleString()}</td>
                      <td style={td}><select value={editData.status} onChange={e => setEditData({ ...editData, status: e.target.value })} style={inlineInp}><option value="unpaid">Unpaid</option><option value="partial">Partial</option><option value="paid">Paid</option></select></td>
                      <td style={td}>{!readOnly && <button onClick={() => saveEditContrib(c.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>}<button onClick={() => setEditingContrib(null)} style={smBtn('#95a5a6')}>Cancel</button></td>
                    </tr>
                  );

                  // INLINE PAY ROW — now includes mode_of_payment and receipt_ref
                  if (payingId === c.id) return (
                    <tr key={c.id} style={{ background: '#eafaf1', borderLeft: '4px solid #27ae60' }}>
                      <td style={td} colSpan={2}><strong style={{ color: '#1e8449' }}>Pay — {c.full_name}</strong></td>
                      <td style={td}>
                        <label style={{ display: 'block', fontSize: '10px', color: '#7f8c8d', marginBottom: '2px' }}>Amount</label>
                        <input type="number" style={inlineInp} value={payData.amount_paid} onChange={e => setPayData({ ...payData, amount_paid: e.target.value })} />
                      </td>
                      <td style={td}>
                        <label style={{ display: 'block', fontSize: '10px', color: '#7f8c8d', marginBottom: '2px' }}>Date</label>
                        <input type="date" style={inlineInp} value={payData.payment_date} onChange={e => setPayData({ ...payData, payment_date: e.target.value })} />
                      </td>
                      <td style={td}>
                        <label style={{ display: 'block', fontSize: '10px', color: '#7f8c8d', marginBottom: '2px' }}>Mode of Payment</label>
                        <select style={inlineInp} value={payData.mode_of_payment} onChange={e => setPayData({ ...payData, mode_of_payment: e.target.value })}>
                          {PAYMENT_MODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                      </td>
                      <td style={td}>
                        <label style={{ display: 'block', fontSize: '10px', color: '#7f8c8d', marginBottom: '2px' }}>Receipt Ref</label>
                        <input style={inlineInp} placeholder='Optional' value={payData.receipt_ref} onChange={e => setPayData({ ...payData, receipt_ref: e.target.value })} />
                      </td>
                      <td style={td}>
                        {!readOnly && <button onClick={() => recordPayment(c.id)} style={{ ...smBtn('#27ae60'), marginRight: '4px' }}>Record</button>}
                        <button onClick={() => setPayingId(null)} style={smBtn('#95a5a6')}>Cancel</button>
                      </td>
                    </tr>
                  );

                  // NORMAL ROW
                  return (
                    <tr key={c.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={td}><strong>{c.full_name}</strong><br /><span style={{ color: '#95a5a6', fontSize: '11px' }}>{c.member_number}</span></td>
                      <td style={td}>{monthNames[(c.calendar_month || 1) - 1]} {c.calendar_year}</td>
                      <td style={td}>{Number(c.amount_due).toLocaleString()}</td>
                      <td style={{ ...td, color: '#27ae60', fontWeight: '600' }}>{Number(c.amount_paid).toLocaleString()}</td>
                      <td style={{ ...td, color: parseFloat(c.balance) > 0 ? '#e74c3c' : '#27ae60', fontWeight: '600' }}>{Number(c.balance).toLocaleString()}</td>
                      <td style={td}>{statusBadge(c.status)}{fineTag(c)}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {!readOnly && c.status !== 'paid' && (
                            <button onClick={() => { setPayingId(c.id); setPayData({ amount_paid: c.balance, payment_date: new Date().toISOString().split('T')[0], mode_of_payment: '', receipt_ref: '' }); }} style={smBtn('#27ae60')}>Pay</button>
                          )}
                          {!readOnly && c.status !== 'paid' && isPastDue(c) && (
                            <button onClick={() => coverByAssociation(c.id, c.full_name)} style={smBtn('#2980b9')}>Cover by Assoc.</button>
                          )}
                          {!readOnly && <button onClick={() => { setEditingContrib(c.id); setEditData({ amount_due: c.amount_due, amount_paid: c.amount_paid, status: c.status }); }} style={smBtn('#e67e22')}>Edit</button>}
                          {c.status !== 'unpaid' && (
                            <button onClick={() => generateContributionReceipt(c, cycle)} style={{ background: '#8e44ad', color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>📄 Receipt</button>
                          )}
                          {!readOnly && c.status === 'paid' && c.paid_by_association && (
                            <button onClick={() => reverseCoverByAssociation(c.id, c.full_name)} style={smBtn('#c0392b')}>Reverse Cover</button>
                          )}
                          {!readOnly && c.status === 'paid' && !c.paid_by_association && (
                            <button onClick={() => deletePayment(c.id, c.full_name)} style={smBtn('#e74c3c')}>Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PAYOUTS TAB */}
      {tab === 'payouts' && (
        <div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#2c3e50', color: 'white' }}>
                  <th style={th}>Slot</th><th style={th}>Member</th><th style={th}>Month</th><th style={th}>Payout Amount</th><th style={th}>Scheduled Date</th><th style={th}>Status</th><th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payouts.length === 0 && <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: '#95a5a6' }}>No payout slots</td></tr>}
                {payouts.map((p, i) => (
                  editingPayout === p.id ? (
                    <tr key={p.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                      <td style={td}>{p.slot_number}</td>
                      <td style={td}>{p.primary_member_name}</td>
                      <td style={td}>Month {p.scheduled_month}</td>
                      <td style={td}><input type="number" value={editData.payout_amount_primary} onChange={e => setEditData({ ...editData, payout_amount_primary: e.target.value })} style={inlineInp} /></td>
                      <td style={td}><input type="date" value={editData.scheduled_date || ''} onChange={e => setEditData({ ...editData, scheduled_date: e.target.value })} style={inlineInp} /></td>
                      <td style={td}><select value={editData.payout_status} onChange={e => setEditData({ ...editData, payout_status: e.target.value })} style={inlineInp}><option value="pending">Pending</option><option value="blocked">Blocked</option><option value="postponed">Postponed</option></select></td>
                      <td style={td}>{!readOnly && <button onClick={() => saveEditPayout(p.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>}<button onClick={() => setEditingPayout(null)} style={smBtn('#95a5a6')}>Cancel</button></td>
                    </tr>
                  ) : (
                    <tr key={p.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={td}><strong>#{p.slot_number}</strong></td>
                      <td style={td}>
                        <div>{p.primary_member_name}</div>
                        {p.secondary_member_name && <div style={{ fontSize: '11px', color: '#666' }}>+ {p.secondary_member_name}</div>}
                      </td>
                      <td style={td}>Month {p.scheduled_month}</td>
                      <td style={{ ...td, fontWeight: '600', color: '#1e8449' }}>UGX {Number(p.payout_amount_primary).toLocaleString()}</td>
                      <td style={td}>{p.scheduled_date?.slice(0, 10) || '—'}</td>
                      <td style={td}>{statusBadge(p.payout_status)}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {!readOnly && p.payout_status !== 'disbursed' && <button onClick={() => openDisburse(p)} style={smBtn('#27ae60')}>Disburse</button>}
                          {!readOnly && p.payout_status !== 'disbursed' && <button onClick={() => { setEditingPayout(p.id); setEditData({ payout_amount_primary: p.payout_amount_primary, scheduled_date: p.scheduled_date?.slice(0, 10) || '', payout_status: p.payout_status }); }} style={smBtn('#e67e22')}>Edit</button>}
                          {p.payout_status === 'disbursed' && <button onClick={async () => { let ded = []; try { ded = (await axios.get(`${API}/slots/${p.id}/deductions`, { headers })).data; } catch (e) {} generatePayoutReceipt(p, cycle, ded); }} style={{ background: '#1a5276', color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>📄 Receipt</button>}
                        </div>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SWAPS TAB */}
      {tab === 'swaps' && (
        <div>
          <div style={{ background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#1a5276' }}>
            <strong>How to record a swap:</strong> Click a row to select Member A (turns orange), then click another row to select Member B.
          </div>
          {!readOnly && showSwapConfirm && slotA && slotB && (
            <div style={{ background: '#fef9e7', border: '2px solid #f39c12', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#e67e22', marginBottom: '12px' }}>Confirm Swap</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {[slotA, slotB].map((s, i) => (
                  <div key={i} style={{ background: '#fff', border: '2px solid #e67e22', borderRadius: '6px', padding: '10px 16px', fontSize: '13px' }}>
                    <div style={{ fontWeight: '700' }}>{s.primary_member_name}</div>
                    <div style={{ color: '#888', fontSize: '11px' }}>Slot #{s.slot_number} — Month {s.scheduled_month}</div>
                  </div>
                ))}
                <div style={{ fontSize: '24px', color: '#e67e22', fontWeight: '700' }}>↔</div>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Effective Date *</label><input type="date" style={inp} value={swapForm.effective_date} onChange={e => setSwapForm({ ...swapForm, effective_date: e.target.value })} /></div>
                <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Agreement Ref</label><input type="text" style={{ ...inp, width: '180px' }} placeholder="e.g. SWAP-001" value={swapForm.agreement_ref} onChange={e => setSwapForm({ ...swapForm, agreement_ref: e.target.value })} /></div>
                {!readOnly && <button onClick={submitSwap} style={btn('#27ae60')}>Confirm Swap</button>}
                <button onClick={clearSwapSelection} style={btn('#95a5a6')}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>{slotA ? `Selected: ${slotA.primary_member_name} (Slot #${slotA.slot_number}) — click another slot` : 'Click any slot to start a swap'}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ background: '#2c3e50', color: 'white' }}><th style={th}>Slot #</th><th style={th}>Member</th><th style={th}>Month</th><th style={th}>Scheduled Date</th><th style={th}>Payout</th><th style={th}>Status</th></tr></thead>
              <tbody>
                {payouts.length === 0 && <tr><td colSpan="6" style={{ ...td, textAlign: 'center', color: '#95a5a6' }}>No slots</td></tr>}
                {payouts.map((p, i) => {
                  const isA = slotA?.id === p.id; const isB = slotB?.id === p.id; const isDisbursed = p.payout_status === 'disbursed';
                  return (
                    <tr key={p.id} onClick={() => !readOnly && !isDisbursed && handleSlotSelect(p)} style={{ background: isA || isB ? '#fef3cd' : i % 2 === 0 ? '#f9f9f9' : 'white', borderLeft: isA ? '4px solid #e67e22' : isB ? '4px solid #f39c12' : '4px solid transparent', cursor: readOnly || isDisbursed ? 'default' : 'pointer', opacity: isDisbursed ? 0.6 : 1 }}>
                      <td style={td}><strong>#{p.slot_number}</strong></td>
                      <td style={td}><div style={{ fontWeight: isA || isB ? '700' : '400' }}>{p.primary_member_name}</div>{p.secondary_member_name && <div style={{ fontSize: '11px', color: '#666' }}>+ {p.secondary_member_name}</div>}{isA && <span style={{ fontSize: '10px', background: '#e67e22', color: 'white', padding: '1px 6px', borderRadius: '3px' }}>SELECTED A</span>}{isB && <span style={{ fontSize: '10px', background: '#f39c12', color: 'white', padding: '1px 6px', borderRadius: '3px' }}>SELECTED B</span>}</td>
                      <td style={td}>Month {p.scheduled_month}</td>
                      <td style={td}>{p.scheduled_date?.slice(0, 10) || '—'}</td>
                      <td style={{ ...td, fontWeight: '600', color: '#1e8449' }}>UGX {Number(p.payout_amount_primary).toLocaleString()}</td>
                      <td style={td}>{statusBadge(p.payout_status)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {swaps.length > 0 && (<>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#2c3e50', margin: '24px 0 8px' }}>Swap History ({swaps.length})</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead><tr style={{ background: '#8e44ad', color: 'white' }}><th style={th}>Member A</th><th style={th}>Member B</th><th style={th}>Original Slots</th><th style={th}>Effective Date</th><th style={th}>Ref</th><th style={th}>Status</th></tr></thead>
                <tbody>{swaps.map((s, i) => (<tr key={s.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}><td style={td}>{s.member_a_name}</td><td style={td}>{s.member_b_name}</td><td style={td}>Slot #{s.original_slot_number_a} ↔ #{s.original_slot_number_b}</td><td style={td}>{s.effective_date?.slice(0, 10) || '—'}</td><td style={td}>{s.agreement_ref || '—'}</td><td style={td}>{statusBadge(s.status)}</td></tr>))}</tbody>
              </table>
            </div>
          </>)}
        </div>
      )}

      {/* TRANSFERS TAB */}
      {tab === 'transfers' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ background: '#fef9e7', border: '1px solid #f39c12', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#7d6608', maxWidth: '600px' }}><strong>Transfer = member exits the cycle.</strong> Their slot passes to the transferee.</div>
            {!readOnly && <button onClick={() => setShowTransferForm(!showTransferForm)} style={btn('#8e44ad')}>{showTransferForm ? 'Cancel' : '+ Record Transfer'}</button>}
          </div>
          {!readOnly && showTransferForm && (
            <form onSubmit={submitTransfer} style={{ background: '#f5eef8', border: '1px solid #d7bde2', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#6c3483', marginBottom: '16px' }}>Record Member Transfer</div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <div style={{ flex: 1, minWidth: '220px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Outgoing Member (Transferor) *</label>
                  <select style={inp} value={transferForm.transferor_registration_id} onChange={e => setTransferForm({ ...transferForm, transferor_registration_id: e.target.value })} required>
                    <option value=''>-- Select member exiting --</option>
                    {activeRegistrations.map(r => <option key={r.id} value={r.id}>{r.full_name} ({r.member_number}) — {parseFloat(r.shares)} share(s)</option>)}
                  </select>
                  {selectedTransferorSlot && <div style={{ marginTop: '8px', background: '#fadbd8', borderRadius: '6px', padding: '8px 12px', fontSize: '12px' }}><strong>Slot #{selectedTransferorSlot.slot_number}</strong> — Month {selectedTransferorSlot.scheduled_month}<br />Payout: UGX {Number(selectedTransferorSlot.payout_amount_primary).toLocaleString()}</div>}
                </div>
                <div style={{ flex: 1, minWidth: '220px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Incoming Member (Transferee) *</label>
                  <select style={inp} value={transferForm.transferee_id} onChange={e => setTransferForm({ ...transferForm, transferee_id: e.target.value })} required>
                    <option value=''>-- Select incoming member --</option>
                    {members.map(m => <option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Effective Date *</label><input type="date" style={inp} value={transferForm.effective_date} onChange={e => setTransferForm({ ...transferForm, effective_date: e.target.value })} required /></div>
                <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Reason</label><input type="text" style={{ ...inp, width: '200px' }} placeholder="e.g. Relocation" value={transferForm.reason} onChange={e => setTransferForm({ ...transferForm, reason: e.target.value })} /></div>
                <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Agreement Ref</label><input type="text" style={{ ...inp, width: '160px' }} placeholder="e.g. XFER-001" value={transferForm.agreement_ref} onChange={e => setTransferForm({ ...transferForm, agreement_ref: e.target.value })} /></div>
                <button type="submit" style={btn('#8e44ad')}>Record Transfer</button>
              </div>
            </form>
          )}
          {transfers.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>No transfers recorded</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ background: '#8e44ad', color: 'white' }}><th style={th}>Transferee</th><th style={th}>Slot</th><th style={th}>Effective Date</th><th style={th}>Reason</th><th style={th}>Ref</th><th style={th}>Status</th></tr></thead>
              <tbody>{transfers.map((t, i) => (<tr key={t.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}><td style={td}>{t.transferee_name}</td><td style={td}>Slot #{payouts.find(p => p.id === t.slot_id)?.slot_number || '—'}</td><td style={td}>{t.effective_date?.slice(0, 10) || '—'}</td><td style={td}>{t.reason || '—'}</td><td style={td}>{t.agreement_ref || '—'}</td><td style={td}>{statusBadge(t.status)}</td></tr>))}</tbody>
            </table>
          )}
        </div>
      )}

      {/* LOTTERY TAB */}
      {tab === 'lottery' && (
        <div>
          {cycle?.status === 'lottery_done' ? (
            <div style={{ background: '#d5f5e3', border: '1px solid #a9dfbf', borderRadius: '8px', padding: '14px 18px', marginBottom: '20px', fontSize: '13px', color: '#1e8449' }}>
              <strong>✓ Lottery completed</strong> — {payouts.length} slots assigned. Go to <strong>Overview</strong> to activate the cycle.
            </div>
          ) : (
            <div style={{ background: '#fef9e7', border: '1px solid #f39c12', borderRadius: '8px', padding: '14px 18px', marginBottom: '20px', fontSize: '13px', color: '#7d6608' }}>
              <strong>Assign slot numbers to each member position.</strong> Each slot number (1–{cycleDetail?.cycle?.total_shares}) must be used exactly once.
            </div>
          )}
          {cycle?.status === 'lottery_pending' && (() => {
            const positions = generateLotteryPositions(); const totalSlots = parseInt(cycleDetail?.cycle?.total_shares || 0);
            const assigned = Object.values(lotteryAssignments).filter(v => v !== ''); const allAssigned = assigned.length === positions.length;
            return (<>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <div style={{ background: '#8e44ad', color: 'white', padding: '10px 16px', borderRadius: '6px', fontSize: '13px' }}>Total Positions: <strong>{positions.length}</strong></div>
                <div style={{ background: assigned.length === positions.length ? '#27ae60' : '#f39c12', color: 'white', padding: '10px 16px', borderRadius: '6px', fontSize: '13px' }}>Assigned: <strong>{assigned.length} / {positions.length}</strong></div>
              </div>
              <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead><tr style={{ background: '#8e44ad', color: 'white' }}><th style={th}>#</th><th style={th}>Member / Position</th><th style={th}>Type</th><th style={th}>Assign Slot Number (1–{totalSlots})</th><th style={th}>Preview</th></tr></thead>
                  <tbody>
                    {positions.map((p, i) => {
                      const val = lotteryAssignments[p.key] || '';
                      const isDupe = val && Object.entries(lotteryAssignments).filter(([k]) => k !== p.key).some(([, v]) => v === val);
                      return (
                        <tr key={p.key} style={{ background: isDupe ? '#fde8e8' : i % 2 === 0 ? '#f9f9f9' : 'white', borderLeft: isDupe ? '4px solid #e74c3c' : '4px solid transparent' }}>
                          <td style={{ ...td, color: '#888', fontSize: '12px' }}>{i + 1}</td>
                          <td style={td}><div style={{ fontWeight: '600' }}>{p.label}</div><div style={{ fontSize: '11px', color: '#888' }}>{p.sublabel}</div></td>
                          <td style={td}><span style={{ background: p.type === 'half_pair' ? '#e67e22' : '#1a5276', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{p.type === 'half_pair' ? 'SHARED' : 'FULL'}</span></td>
                          <td style={td}><input type='number' min='1' max={totalSlots} value={val} onChange={e => setLotteryAssignments({ ...lotteryAssignments, [p.key]: e.target.value })} style={{ padding: '6px 10px', border: isDupe ? '2px solid #e74c3c' : '1px solid #ddd', borderRadius: '4px', fontSize: '14px', width: '80px', textAlign: 'center', fontWeight: '700' }} placeholder='—' />{isDupe && <span style={{ color: '#e74c3c', fontSize: '11px', marginLeft: '6px' }}>Duplicate!</span>}</td>
                          <td style={{ ...td, color: '#888', fontSize: '12px' }}>{val ? `Slot #${val} — Month ${Math.ceil(parseInt(val) / (cycleDetail?.cycle?.payouts_per_month || 1))}` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {!readOnly && <button onClick={submitLottery} disabled={!allAssigned} style={{ ...btn(allAssigned ? '#27ae60' : '#95a5a6'), cursor: allAssigned ? 'pointer' : 'not-allowed', fontSize: '14px', padding: '10px 24px' }}>✓ Confirm & Save Lottery Draw</button>}
                <button onClick={() => setLotteryAssignments({})} style={btn('#95a5a6')}>Reset</button>
                {!allAssigned && <span style={{ color: '#e67e22', fontSize: '12px' }}>{positions.length - assigned.length} position(s) still need slot numbers</span>}
              </div>
            </>);
          })()}
          {cycle?.status === 'lottery_done' && payouts.length > 0 && (
            <div>
              {/* Preview start date + PDF export */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', background: '#f0f4ff', border: '1px solid #aed6f1', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#1a5276', marginBottom: '4px' }}>Cycle starts from (for month display)</label>
                  <input type="date" style={{ padding: '7px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }}
                    value={previewStartDate}
                    onChange={e => setPreviewStartDate(e.target.value)} />
                </div>
                <button onClick={generateLotteryPDF} style={{ background: '#8e44ad', color: 'white', border: 'none', padding: '8px 18px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                  📄 Export Draw PDF
                </button>
                <div style={{ fontSize: '12px', color: '#666', alignSelf: 'center' }}>
                  Set the start date to preview correct calendar months before activating.
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead><tr style={{ background: '#8e44ad', color: 'white' }}>
                    <th style={th}>Slot #</th><th style={th}>Member</th><th style={th}>Type</th><th style={th}>Payout Month</th><th style={th}>Payout Amount</th>
                  </tr></thead>
                  <tbody>{[...payouts].sort((a, b) => a.slot_number - b.slot_number).map((p, i) => (
                    <tr key={p.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={{ ...td, fontWeight: '700', color: '#8e44ad' }}>#{p.slot_number}</td>
                      <td style={td}>
                        <div style={{ fontWeight: '600' }}>{p.primary_member_name}</div>
                        {p.secondary_member_name && <div style={{ fontSize: '11px', color: '#666' }}>+ {p.secondary_member_name}</div>}
                      </td>
                      <td style={td}>
                        <span style={{ background: p.slot_type === 'half_pair' ? '#e67e22' : '#1a5276', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                          {p.slot_type === 'half_pair' ? 'SHARED' : 'FULL'}
                        </span>
                      </td>
                      <td style={td}>{getCalendarMonth(p.scheduled_month)}</td>
                      <td style={{ ...td, fontWeight: '600', color: '#1e8449' }}>UGX {Number(p.payout_amount_primary).toLocaleString()}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* DEFAULTERS TAB */}
      {tab === 'defaulters' && (
        <div>
          {defaulters.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: '#95a5a6', fontSize: '15px' }}>No active ROSCA defaulters</div> : (<>
            <div style={{ background: '#fdf3f3', border: '1px solid #e74c3c', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px' }}><strong style={{ color: '#c0392b' }}>{defaulters.length} member(s) with active ROSCA fines</strong></div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead><tr style={{ background: '#c0392b', color: 'white' }}><th style={th}>Member</th><th style={th}>Tier</th><th style={th}>Original</th><th style={th}>Fine Balance</th><th style={th}>Days Overdue</th><th style={th}>Tier 2 Starts</th><th style={th}>Status</th></tr></thead>
                <tbody>
                  {defaulters.map((d, i) => {
                    const isT2 = d.tier_2_start && new Date() >= new Date(d.tier_2_start);
                    return (<tr key={d.id} style={{ background: i % 2 === 0 ? '#fdf3f3' : 'white', borderLeft: `4px solid ${isT2 ? '#c0392b' : '#e67e22'}` }}>
                      <td style={td}><strong>{d.full_name}</strong><br /><span style={{ color: '#95a5a6', fontSize: '11px' }}>{d.member_number}</span></td>
                      <td style={td}><span style={{ background: isT2 ? '#c0392b' : '#e67e22', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{isT2 ? 'TIER 2 (20%/mo)' : 'TIER 1 (10%)'}</span></td>
                      <td style={td}>UGX {Number(d.original_amount || d.amount).toLocaleString()}</td>
                      <td style={{ ...td, fontWeight: '700', color: '#c0392b' }}>UGX {Number(d.balance).toLocaleString()}</td>
                      <td style={td}>{d.days_overdue || '—'} days</td>
                      <td style={td}>{d.tier_2_start?.slice(0, 10) || '—'}</td>
                      <td style={td}>{statusBadge(d.status)}</td>
                    </tr>);
                  })}
                </tbody>
              </table>
            </div>
          </>)}
        </div>
      )}
    </div>
  );
}

const th = { padding: '10px', textAlign: 'left', fontSize: '12px' };
const td = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };

export default Rosca;